import { Input } from 'components/Input'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { InputProps } from '@ur-solutions/react-components'
import { useGoogleAutocomplete } from 'utils/hooks'
import { useGoogleLoader } from 'utils/hooks/useGoogleLoader'
import { GoogleCountryCodes } from 'utils/consts'
import { UseGoogleAutocompleteOptions } from 'utils/hooks/useGoogleAutocomplete'

interface PlacePickerProps extends Omit<InputProps, 'type'> {}

export const PlacePicker = React.forwardRef<HTMLInputElement, PlacePickerProps>(
  ({ value, onChange, ...props }, ref) => {
    const { scriptLoaded } = useGoogleLoader()

    const prevPlacename = useRef<string | null>(null)
    const descriptionPlaceIdMap = useRef(new Map<string, string>())
    const [triggerGeocoding, setTriggerGeocoding] = useState(false)

    const handleFindPlacename = useCallback(() => {
      prevPlacename.current = value
      const service = new google.maps.Geocoder()
      const placeIdValue = descriptionPlaceIdMap.current.get(
        prevPlacename.current
      )
      const geocodeOptions =
        // if we have a placeID from the autocomplete this simply needs to be a lookup with the ID, otherwise we try to find something with geocoding
        placeIdValue !== undefined
          ? { placeId: placeIdValue }
          : { address: value }

      service.geocode(geocodeOptions, function (results, status) {
        if (status !== google.maps.GeocoderStatus.OK) return
        const [place] = results ?? [null]
        if (!place || !place.formatted_address) return
        onChange(place.formatted_address)
      })
    }, [value, prevPlacename, onChange])

    useEffect(() => {
      prevPlacename.current = value
      if (typeof google === 'undefined' || value.trim() === '') return

      if (triggerGeocoding) {
        handleFindPlacename()
        setTriggerGeocoding(false)
      }
    }, [value, prevPlacename, triggerGeocoding, handleFindPlacename])

    const predictions = useGoogleAutocomplete(value, {
      componentRestrictions: {
        country: [
          // country code restrictions we use for the autocomplete API
          GoogleCountryCodes.Norway,
          GoogleCountryCodes.Sweden,
          GoogleCountryCodes.Denmark,
          GoogleCountryCodes.Finland,
          GoogleCountryCodes.Estonia,
          GoogleCountryCodes.Latvia,
          GoogleCountryCodes.RussianFederation,
          GoogleCountryCodes.Lithuania,
          GoogleCountryCodes.Poland,
          GoogleCountryCodes.Germany,
          GoogleCountryCodes.Belarus,
          GoogleCountryCodes.Ukraine,
          GoogleCountryCodes.CzechRepublic,
        ],
      },
    } as UseGoogleAutocompleteOptions)

    const autoCompleteSource = predictions.predictions?.map(pred => {
      // The description field is the human readable value, whereas if we pick an option from here
      // we want to use the ID related to said description. We do this by making use of a hashmap which encodes a
      // string with an ID which we can use when we want to perform geocoding. If the key already exists we don't bother.
      if (!descriptionPlaceIdMap.current.has(pred.description)) {
        descriptionPlaceIdMap.current.set(pred.description, pred.place_id)
      }
      return pred.description
    })

    function handlePlacenameKeyDown(
      event: React.KeyboardEvent<HTMLInputElement>
    ) {
      const { key } = event
      if (key === 'Enter') {
        setTriggerGeocoding(true)
      }
    }

    function handleAutoCompleteItemSelect(placename: string) {
      onChange(placename)
      setTriggerGeocoding(true)
    }
    if (!scriptLoaded) return null

    return (
      <Input
        ref={ref}
        value={value}
        autoCompleteSource={autoCompleteSource}
        onAutoCompleteItemSelect={handleAutoCompleteItemSelect}
        onChange={onChange}
        onKeyDown={handlePlacenameKeyDown}
        {...props}
      />
    )
  }
)
